import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerSwipeArea,
  DrawerTitle,
} from '@/components/ui/drawer';
import { MiniCardCarousel } from '@/components/shared/shop/ui/MiniCardCarousel';
import { ScreenHead } from '@/components/shared/screen/head';
import { getLanguageFromLocale, useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import {
  getRidePickupPoints,
  RidePickupPoint,
  RidePickupPointsResponse,
} from '@/services/platform/data/booking/rides/pickupPoints';
import { RideMapBackdrop } from '@/components/shared/booking/engine/flows/range-rover/map/rideMapBackdrop';
import { geocodeAddress } from '@/components/shared/booking/engine/flows/range-rover/map/geocodeAddress';
import {
  searchPlaces,
  fetchPlaceDetails,
  createSessionToken,
} from '@/components/shared/booking/engine/flows/range-rover/map/searchPlaces';
import { LocationMiniCard, LocationListRow } from './locationMiniCard';

const LISBON_CENTER = { lat: 38.7223, lng: -9.1393 };

const GOOGLE_PLACES_SOURCE = 'google_places';
const PLACES_SEARCH_DEBOUNCE_MS = 350;
const PLACES_SEARCH_MIN_LENGTH = 3;

const EMPTY_RESPONSE: RidePickupPointsResponse = {
  addressBook: [],
  venues: [],
  stores: [],
  recentTrips: [],
};

export interface LocationPickerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  pickupPoint?: RidePickupPoint | null;
  dropoffPoint?: RidePickupPoint | null;
  /** Which of pickup/dropoff this picker is choosing — governs map centering and the highlighted selection. */
  target: 'pickup' | 'dropoff';
  onSelect: (point: RidePickupPoint) => void;
}

function toMarker(point?: RidePickupPoint | null) {
  return point?.lat != null && point?.lng != null ? { lat: point.lat, lng: point.lng } : null;
}

function matches(point: RidePickupPoint, query: string): boolean {
  const normalised = query.trim().toLowerCase();
  if (!normalised) return true;
  return [point.name, point.city, point.address]
    .filter(Boolean)
    .some(value => value!.toLowerCase().includes(normalised));
}

function rowKey(point: RidePickupPoint, index: number): string {
  return point.referenceId ?? `${point.source}-${point.name}-${index}`;
}

function isSamePoint(a: RidePickupPoint, b?: RidePickupPoint | null): boolean {
  if (!b) return false;
  if (a.referenceId != null || b.referenceId != null) return a.referenceId === b.referenceId;
  return a.source === b.source && a.name === b.name;
}

export function LocationPicker({
  isOpen,
  onClose,
  title,
  subtitle,
  pickupPoint,
  dropoffPoint,
  target,
  onSelect,
}: LocationPickerProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const selectedPoint = target === 'pickup' ? pickupPoint : dropoffPoint;
  const [response, setResponse] = useState<RidePickupPointsResponse>(EMPTY_RESPONSE);
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [geocodingKey, setGeocodingKey] = useState<string | null>(null);
  const [pendingPoint, setPendingPoint] = useState<RidePickupPoint | null>(null);
  const [placeResults, setPlaceResults] = useState<RidePickupPoint[]>([]);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Measured so the drawer's max-height can be capped against the real header, rather than a
  // flat viewport fraction — keeps the persistent header/back button structurally uncoverable.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    setHeaderHeight(el.offsetHeight);
    const observer = new ResizeObserver(([entry]) => setHeaderHeight(entry.contentRect.height));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setIsExpanded(true);
    setGeocodingKey(null);
    setPlaceResults([]);
    sessionTokenRef.current = null;
    getRidePickupPoints(locale).then(setResponse);
  }, [isOpen, locale]);

  // Debounced Google Places search for addresses outside the curated catalogue above.
  // A session token is created once per active search (cleared whenever the query is
  // emptied) and reused for every keystroke's request so Google bills them as one session.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < PLACES_SEARCH_MIN_LENGTH) {
      setPlaceResults([]);
      sessionTokenRef.current = null;
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = (await createSessionToken()) ?? null;
      }
      if (cancelled || !sessionTokenRef.current) return;

      const results = await searchPlaces(trimmed, sessionTokenRef.current, getLanguageFromLocale(locale));
      if (!cancelled) setPlaceResults(results);
    }, PLACES_SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, locale]);

  // Address-book entries and Google Places results are both arbitrary addresses that may
  // fall outside curated JNcQUOI locations, so they get a confirmation step (tailored fare
  // disclaimer) before actually applying the selection — venues/stores/recent trips select immediately.
  const confirmAndSelect = (point: RidePickupPoint) => {
    if (point.source === 'address_book' || point.source === GOOGLE_PLACES_SOURCE) {
      setPendingPoint(point);
      return;
    }
    onSelect(point);
  };

  // Google Places predictions carry no coordinates until their full details are fetched;
  // address-book entries saved before geocoding was supported come back with lat/lng = null too —
  // resolve either via the appropriate Places/Geocoder call at selection time.
  const handleSelect = async (point: RidePickupPoint, key: string) => {
    if (point.lat != null && point.lng != null) {
      confirmAndSelect(point);
      return;
    }

    if (point.source === GOOGLE_PLACES_SOURCE) {
      if (!point.referenceId) return;
      setGeocodingKey(key);
      const details = await fetchPlaceDetails(point.referenceId, getLanguageFromLocale(locale));
      setGeocodingKey(null);
      if (!details) return;

      confirmAndSelect(details);
      return;
    }

    if (!point.address) return;

    setGeocodingKey(key);
    const coords = await geocodeAddress(
      `${point.address},${point.postalCode ?? ''} ${point.city}, ${point.country ?? ''} (${point.countryCode})`
    );
    setGeocodingKey(null);
    if (!coords) return;

    confirmAndSelect({ ...point, lat: coords.lat, lng: coords.lng });
  };

  const handleConfirmPendingPoint = () => {
    if (pendingPoint) onSelect(pendingPoint);
    setPendingPoint(null);
  };

  const hasQuery = query.trim().length > 0;

  const addressBook = useMemo(() => response.addressBook.filter(p => matches(p, query)), [response, query]);
  const venues = useMemo(() => response.venues.filter(p => matches(p, query)), [response, query]);
  const stores = useMemo(() => response.stores.filter(p => matches(p, query)), [response, query]);
  const recentTrips = useMemo(() => response.recentTrips.filter(p => matches(p, query)), [response, query]);

  const renderRows = (points: RidePickupPoint[]) =>
    points.map((point, index) => {
      const key = rowKey(point, index);
      return (
        <LocationListRow
          key={key}
          point={point}
          isLoading={geocodingKey === key}
          onClick={() => handleSelect(point, key)}
        />
      );
    });

  if (!isOpen) return null;

  const selectedMarker = toMarker(selectedPoint);
  const mapCenter = selectedMarker ?? LISBON_CENTER;
  const pickupMarker = toMarker(pickupPoint);
  const dropoffMarker = toMarker(dropoffPoint);

  return (
    <>
      {/* Persistent page chrome — portalled straight to <body>, same as it was previously reaching
          the viewport via the Drawer's own Portal, so `fixed` anchors to the real viewport rather
          than whatever transformed ancestor this component happens to render under. Always
          mounted regardless of drawer/swipe state — the back arrow is the only way to truly leave
          this screen, the drawer below only collapses. */}
      {createPortal(
        <>
          <RideMapBackdrop
            center={mapCenter}
            pickupMarker={pickupMarker}
            dropoffMarker={dropoffMarker}
            className="fixed inset-0"
          />
          {/* z-90: above the Drawer's own Viewport/Popup (hardcoded z-80 in drawer.tsx), so the
              sheet — full height or not — can never paint over the header/back button. */}
          <div ref={headerRef} className="pt-safe-area fixed inset-x-0 top-0 z-90 bg-white">
            <ScreenHead
              title={t('App.Labels.RangeRover')}
              onBack={onClose}
              ariaLabel={t('App.Labels.GoBack')}
              className="pt-0"
            />
          </div>
        </>,
        document.body
      )}

      <Drawer open={isExpanded} onOpenChange={setIsExpanded}>
        {/* Peek bar — a Drawer-root child, so it stays mounted (and swipeable/tappable open)
            even while the sheet itself is collapsed. */}
        <DrawerSwipeArea className="pb-safe-area bg-jncquoi-neutral-00 fixed inset-x-0 bottom-0 z-40 flex flex-col rounded-t-sm px-4 pt-4 drop-shadow-[0px_0px_50px_rgba(0,0,0,0.05)]">
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="flex cursor-pointer flex-col gap-2 pb-8 text-left"
          >
            <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[32px] tracking-[0.5px]">
              {title}
            </h2>
            {subtitle && (
              <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[16px] tracking-[0.25px]">
                {subtitle}
              </p>
            )}
          </button>
        </DrawerSwipeArea>

        <DrawerContent
          overlayClassName="bg-transparent"
          className="px-4 pt-8 pb-10.5 drop-shadow-[0px_0px_50px_rgba(0,0,0,0.05)]"
          style={
            { '--drawer-content-max-height': `min(70vh, calc(100svh - ${headerHeight}px))` } as CSSProperties
          }
        >
          {/* Header block — spacing matches the Figma "FloatingSheet" frame (node 6921:65823)
              exactly: gap-2 (8px) between title row and subtitle, no swipe-handle pill (the
              design has none; swipe-to-collapse still works anywhere on the sheet). */}
          <div className="flex shrink-0 flex-col gap-2">
            <div className="flex items-center gap-8">
              <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-[32px] tracking-[0.5px]">
                {title}
              </h2>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                aria-label={t('App.Labels.Close')}
                className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full"
              >
                <Icon
                  name="xmark"
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000"
                />
              </button>
            </div>
            {subtitle && (
              <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[16px] tracking-[0.25px]">
                {subtitle}
              </p>
            )}
          </div>

          {/* `data-base-ui-swipe-ignore` so dragging over the search box/results scrolls
              instead of dragging the sheet. */}
          <div
            data-base-ui-swipe-ignore
            className="content-location mt-8 flex min-h-0 flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto"
          >
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={title ?? t('App.Labels.SearchAddress')}
              rightIcon={
                hasQuery && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label={t('App.Labels.Close')}
                    className="cursor-pointer"
                  >
                    <Icon name="xmark-circle" width={20} height={20} className="text-jncquoi-neutral-1000" />
                  </button>
                )
              }
            />

            {hasQuery ? (
              <>
                {addressBook.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Icon name="star" width={16} height={16} className="text-jncquoi-neutral-1000" />
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                        {t('App.Labels.FromYourAddressBook')}
                      </span>
                    </div>
                    <div className="flex flex-col">{renderRows(addressBook)}</div>
                  </div>
                )}

                {venues.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                      {t('App.Labels.JNcQUOIVenues')}
                    </span>
                    <div className="flex flex-col">{renderRows(venues)}</div>
                  </div>
                )}

                {stores.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                      {t('App.Labels.FashionClinicStores')}
                    </span>
                    <div className="flex flex-col">{renderRows(stores)}</div>
                  </div>
                )}

                {recentTrips.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Icon name="clock" width={16} height={16} className="text-jncquoi-neutral-1000" />
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                        {t('App.Labels.RecentTrips')}
                      </span>
                    </div>
                    <div className="flex flex-col">{renderRows(recentTrips)}</div>
                  </div>
                )}

                {placeResults.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Icon name="map-pin" width={16} height={16} className="text-jncquoi-neutral-1000" />
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                        {t('App.Labels.SearchOnTheMap')}
                      </span>
                    </div>
                    <div className="flex flex-col">{renderRows(placeResults)}</div>
                  </div>
                )}
              </>
            ) : (
              <>
                {addressBook.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Icon name="star" width={16} height={16} className="text-jncquoi-neutral-1000" />
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                        {t('App.Labels.FromYourAddressBook')}
                      </span>
                    </div>
                    <MiniCardCarousel
                      items={addressBook}
                      cardStep={157}
                      renderItem={(point, index) => {
                        const key = rowKey(point, index);
                        return (
                          <LocationMiniCard
                            point={point}
                            className="bg-jncquoi-neutral-50"
                            isLoading={geocodingKey === key}
                            isSelected={isSamePoint(point, selectedPoint)}
                            onClick={() => handleSelect(point, key)}
                          />
                        );
                      }}
                    />
                  </div>
                )}

                {venues.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                      {t('App.Labels.JNcQUOIVenues')}
                    </span>
                    <MiniCardCarousel
                      items={venues}
                      cardStep={157}
                      renderItem={(point, index) => (
                        <LocationMiniCard
                          point={point}
                          isSelected={isSamePoint(point, selectedPoint)}
                          onClick={() => handleSelect(point, rowKey(point, index))}
                        />
                      )}
                    />
                  </div>
                )}

                {stores.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                      {t('App.Labels.FashionClinicStores')}
                    </span>
                    <MiniCardCarousel
                      items={stores}
                      cardStep={157}
                      renderItem={(point, index) => (
                        <LocationMiniCard
                          point={point}
                          isSelected={isSamePoint(point, selectedPoint)}
                          onClick={() => handleSelect(point, rowKey(point, index))}
                        />
                      )}
                    />
                  </div>
                )}

                {recentTrips.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Icon name="clock" width={16} height={16} className="text-jncquoi-neutral-1000" />
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                        {t('App.Labels.RecentTrips')}
                      </span>
                    </div>
                    <div className="flex flex-col">{renderRows(recentTrips)}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Address-book selections may fall outside curated JNcQUOI locations — confirm before applying. */}
      <Drawer
        open={!!pendingPoint}
        onOpenChange={open => {
          if (!open) setPendingPoint(null);
        }}
        showSwipeHandle={false}
      >
        <DrawerContent className="bg-[#1a1a1a] pt-4">
          <DrawerHeader className="shrink-0 px-4 pt-4">
            <p className="font-pp-neue-montreal text-center text-[14px] tracking-[0.5px] text-white uppercase">
              {t('App.Labels.Confirmation')}
            </p>
          </DrawerHeader>

          <div className="flex flex-1 flex-col gap-4 px-4 py-6">
            <DrawerTitle className="font-pp-neue-montreal text-[24px] leading-tight font-semibold text-white">
              {t('App.Labels.ContinueBeyondJNcQUOI')}
            </DrawerTitle>
            <DrawerDescription className="font-pp-neue-montreal text-[16px] leading-normal tracking-[0.25px] text-[#eaeaea]">
              {t('App.Labels.OutsideJNcQUOILocationsDescription')}
            </DrawerDescription>
          </div>

          <DrawerFooter className="flex shrink-0 flex-col gap-2 border-t border-[#c8c8c8]/30 px-4 pt-6 pb-10">
            <Button
              variant="primary-solid"
              palette="white"
              size="lg"
              className="w-full"
              onClick={handleConfirmPendingPoint}
            >
              {t('App.Labels.Confirm')}
            </Button>
            <Button
              variant="tertiary-link"
              palette="white"
              size="sm"
              className="w-full"
              onClick={() => setPendingPoint(null)}
            >
              {t('App.Labels.ChangeDestination')}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
